import { forwardRef, type ChangeEvent, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

export interface SwitchProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange'
> {
  label?: ReactNode;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onCheckedChange?: (checked: boolean) => void;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, checked, disabled, onChange, onCheckedChange, className, ...rest },
  ref
) {
  return (
    <label
      className={cn(
        'phx-check-label gap-phx-3 text-phx-md inline-flex items-center select-none',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className
      )}
      data-disabled={disabled ? '' : undefined}
    >
      <span className={cn('phx-switch relative inline-flex', checked && 'is-on')}>
        <input
          ref={ref}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => {
            onChange?.(event);
            onCheckedChange?.(event.target.checked);
          }}
          className="peer absolute inset-0 h-full w-full cursor-[inherit] opacity-0"
          {...rest}
        />
        <span
          className={cn(
            'phx-switch-track relative inline-block h-[14px] w-[26px] rounded-[7px]',
            'peer-focus-visible:shadow-phx-focus transition-colors',
            checked ? 'bg-phx-primary' : 'bg-phx-border-3'
          )}
        >
          <span
            className={cn(
              'phx-switch-thumb absolute top-[1px] left-[1px] h-[12px] w-[12px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-transform',
              checked && 'translate-x-[12px]'
            )}
          />
        </span>
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
});
